import { useCallback, useContext, useState } from 'react'

import type { FC } from 'react'

import AstaModal from '@/components/shared/AstaModal/AstaModal'
import CopyAndMoveForm from '@/components/shared/CopyAndMove/CopyAndMoveForm'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { AuthContext } from '@/contexts/auth'
import type { SelectedAppProps } from '@/contexts/selectedApp'
import { WithSelectedApp } from '@/contexts/selectedApp'
import { useAssets } from '@/hooks/App/useAssets'
import { canCreate } from '@/pages/Admin/helpers'
import type { Asset, AssetWithParent } from '@/shared/types'

interface Props {
	type: Asset['type']
	asset: AssetWithParent
	onClose: () => void
}

const MoveAssetMenu: FC<Props & SelectedAppProps> = ({
	selectedApp,
	type,
	asset,
	onClose,
}) => {
	const [open, setOpen] = useState(false)

	const { user } = useContext(AuthContext)
	const { updateAssetParent } = useAssets(type)

	const onMove = useCallback(
		(name: string, newParent: string) => {
			updateAssetParent.mutate({
				pathParams: {
					id: asset._id,
					type,
					appId: asset.parent._id,
				},
				body: { newParent, name },
			})

			onClose()
		},
		[onClose, updateAssetParent, asset, type]
	)

	const _onOpen = useCallback(() => {
		setOpen(true)
	}, [])

	const _onClose = useCallback(() => {
		setOpen(false)
		onClose()
	}, [onClose, setOpen])

	if (!canCreate(asset.parent.role) && !user?.isAdmin) return null

	return (
		<>
			<DropdownMenuItem
				onClick={e => {
					e.preventDefault()
					e.stopPropagation()
					_onOpen()
				}}
			>
				Move to
			</DropdownMenuItem>
			<AstaModal onClose={_onClose} isOpen={open}>
				<CopyAndMoveForm
					selectedApp={selectedApp}
					onSave={onMove}
					onClose={_onClose}
					name={asset.name}
					isAdmin={user?.isAdmin}
					type="move"
					resource={asset}
				/>
			</AstaModal>
		</>
	)
}

export default WithSelectedApp({})(MoveAssetMenu)
